import type { Vehicle } from '@cobliteam/common-types';
import { useVehicle } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SelectItemVehicle from './components/SelectItemVehicle';
import { formatVehicleLabel } from './utils';

export interface SelectVehicleProps {
  /** Currently selected vehicle UUID. Looked up against the fetched list to render the trigger label. */
  value?: string;
  /** Callback fired with the full Vehicle when a row is picked; undefined when cleared. */
  onChange: (vehicle?: Vehicle) => void;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Trigger label when no vehicle is selected. Defaults to the `multiselects:vehicle.title` i18n key. */
  title?: string;
  /** Render dropdown in a portal. Default false, matching MultiSelectVehicles. */
  portal?: boolean;
  /** Optional vehicle group ids to scope the list — forwarded to `useVehicle().getVehicles`. */
  vehicleGroupIds?: string[];
  /** Optional client-side filter on top of the fetched list (e.g., only show camera-equipped vehicles). */
  filterRule?: (vehicle: Vehicle) => boolean;
}

const matchesSearch = (vehicle: Vehicle, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    (vehicle.label?.toLowerCase().includes(q) ?? false) ||
    (vehicle.alias?.toLowerCase().includes(q) ?? false) ||
    (vehicle.licensePlate?.toLowerCase().includes(q) ?? false) ||
    (vehicle.model?.name?.toLowerCase().includes(q) ?? false) ||
    (vehicle.model?.brand?.toLowerCase().includes(q) ?? false)
  );
};

const sortByLabel = (a: Vehicle, b: Vehicle) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase());

const SelectVehicle = ({ className, value, disabled, size = 'sm', title, portal = false, vehicleGroupIds, filterRule, onChange }: SelectVehicleProps) => {
  const { t } = useTranslation('multiselects');

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [forceClosed, setForceClosed] = useState(false);

  const { data, isFetching } = useVehicle().getVehicles({}, vehicleGroupIds);

  const vehicles = useMemo(() => {
    const filtered = filterRule ? (data ?? []).filter(filterRule) : (data ?? []);
    return filtered.slice().sort(sortByLabel);
  }, [data, filterRule]);

  const selected = useMemo(() => (value ? vehicles.find((v) => v.id === value) : undefined), [vehicles, value]);

  const displayed = useMemo(() => {
    if (!search?.trim()) return vehicles;
    return vehicles.filter((v) => matchesSearch(v, search));
  }, [vehicles, search]);

  // Reset the dropdown-close signal right after it fires, so the next open isn't pre-closed.
  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const handleSelect = useCallback(
    (vehicle: Vehicle) => {
      onChange(vehicle);
      setSearch(undefined);
      setForceClosed(true);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(undefined);
    setSearch(undefined);
    setForceClosed(true);
  }, [onChange]);

  const triggerLabel = selected ? formatVehicleLabel(selected) : title || t('vehicle.title');

  return (
    <Dropdown
      forceClose={forceClosed}
      portal={portal}
      overlay={
        <Dropdown.Overlay>
          {selected && (
            <Dropdown.Header className='flex items-center justify-between px-2'>
              <Button type='button' variant='ghost' size='sm' onClick={handleClear}>
                {t('vehicles.clear_selection')}
              </Button>
            </Dropdown.Header>
          )}
          <Dropdown.Search className='px-2'>
            <InputSearch placeholder={t('vehicles.search_placeholder')} value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='px-2'>
            <Dropdown.List<Vehicle>
              heightList={300}
              heightItem={32}
              data={displayed}
              isLoading={isFetching}
              loadingComponent={<Dropdown.Loading />}
              placeholderComponent={<Dropdown.Placeholder icon='cb-placeholder-car' text={t('vehicles.no_vehicles_found')} />}
              itemComponent={(vehicle) => <SelectItemVehicle key={vehicle.id} vehicle={vehicle} isSelected={selected?.id === vehicle.id} onClick={() => handleSelect(vehicle)} />}
            />
          </Dropdown.Body>
        </Dropdown.Overlay>
      }
    >
      <Button
        size={size}
        variant='ghost'
        startIcon={<Icon name='truck' className='shrink-0' />}
        disabled={disabled}
        className={cn('min-w-0', className)}
        data-testid='vehicle-select'
      >
        <span className='block min-w-0 truncate text-left'>{triggerLabel}</span>
      </Button>
    </Dropdown>
  );
};

export default SelectVehicle;
